import { useEffect, useState } from "react";
import { api } from "../utils/api";
import ProductCard from "../components/ProductCard";
import { useLocation } from "react-router-dom";

export default function Products() {
  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");
  
  const PRODUCTS_PER_PAGE = 12; // Show 12 initially
  const [currentPage, setCurrentPage] = useState(1);

  const location = useLocation();
  
  // Read category from ?category=
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const cat = q.get("category");
    if (cat) setSelectedCategory(cat);
  }, [location.search]);

  // Fetch all products + categories once
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const items = await api.getProducts();
      setAllProducts(items || []);
      
      const cats = await api.getCategories();
      setCategories(cats?.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, search, sort]);

  // ------------- FILTERING -------------
  const filtered = allProducts
    .filter((p) =>
      selectedCategory ? p.category === selectedCategory : true
    )
    .filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  // ------------- SORTING -------------
  const sorted = [...filtered].sort((a, b) => {
    if (sort === "low-high") return a.price - b.price;
    if (sort === "high-low") return b.price - a.price;
    return 0;
  });

  // ------------- PAGINATION -------------
  const totalProducts = sorted.length;
  const displayCount = currentPage * PRODUCTS_PER_PAGE;
  const productsToShow = sorted.slice(0, displayCount);
  const hasMore = displayCount < totalProducts;

  const handleLoadMore = () => {
    setLoadingMore(true);
    // Simulate slight delay for better UX
    setTimeout(() => {
      setCurrentPage(prev => prev + 1);
      setLoadingMore(false);
    }, 300);
  };

  return (
    <div className="max-w-7xl mx-auto mt-6 md:mt-10 px-4 space-y-6 md:space-y-10">
      <h1 className="text-3xl md:text-4xl font-bold">All Products 🛍️</h1>

      {/* ---------------- FILTER BAR ---------------- */}
      <div className="bg-white shadow p-4 rounded-lg space-y-3 md:space-y-0 md:flex md:flex-wrap gap-4 items-center md:justify-between">

        {/* Category Filter */}
        <div className="w-full md:w-auto">
          <label className="block md:hidden text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full md:w-auto border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="w-full md:w-auto md:flex-1 md:max-w-xs">
          <label className="block md:hidden text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {/* Sorting */}
        <div className="w-full md:w-auto">
          <label className="block md:hidden text-sm font-medium text-gray-700 mb-1">
            Sort By
          </label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="w-full md:w-auto border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Sort By</option>
            <option value="low-high">Price: Low → High</option>
            <option value="high-low">Price: High → Low</option>
          </select>
        </div>
      </div>

      {/* Results Count */}
      {!loading && (
        <div className="flex justify-between items-center">
          <p className="text-gray-600">
            Showing {productsToShow.length} of {totalProducts} product{totalProducts !== 1 ? "s" : ""}
          </p>
          {(selectedCategory || search) && (
            <button
              onClick={() => {
                setSelectedCategory("");
                setSearch("");
              }}
              className="text-purple-600 hover:text-purple-700 text-sm font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* ---------------- PRODUCT GRID ---------------- */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600 text-lg mb-4">No products found</p>
          {(selectedCategory || search) && (
            <button
              onClick={() => {
                setSelectedCategory("");
                setSearch("");
              }}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Clear filters to see all products
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {productsToShow.map((p) => (
              <ProductCard key={p._id} product={p} />
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center py-8">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Loading...
                  </span>
                ) : (
                  `Load More (${totalProducts - productsToShow.length} remaining)`
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}